import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { buildTaskVariables } from 'services/tasks/tasks';
import { extractRelationshipVariables } from 'support/helpers/relationships/relationships';
import { interpolateString } from 'support/helpers/strings/interpolateString';
import { RelationshipV2, TaskDTO } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { ConnectionSetupTableRow, Ownership, TaskStatus } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { StepStatus } from 'components/Display/Steps/Steps';

type Params = {
  stepStatus: StepStatus;
  tasks: Array<TaskDTO>;
  currentPartnerId: string | undefined;
  relationship: RelationshipV2 | undefined;
  routeTo: ReturnType<typeof useConnectionNavigation>['routeTo'];
  partnerId: string;
  relationshipId: string | undefined;
};

const TASK_STATE_TO_STATUS: Record<TaskDTO['state'], TaskStatus> = {
  COMPLETED: 'DONE',
  BLOCKED: 'REJECTED',
  IN_REVIEW: 'IN_REVIEW',
  PENDING: 'TODO',
};

const getOwnership = (task: TaskDTO, currentPartnerId: string | undefined): Ownership => {
  if (!task.responsible_partner) {
    return 'procuros';
  }

  if (task.responsible_partner.id === currentPartnerId) {
    return 'you';
  }

  return 'trade_partner';
};

export const useTasksStep = ({
  stepStatus,
  tasks,
  currentPartnerId,
  relationship,
  routeTo,
  partnerId,
  relationshipId,
}: Params) => {
  const { t } = useTranslation();

  const rows: Array<ConnectionSetupTableRow> = useMemo(
    () =>
      tasks.map((task) => {
        const responsiblePartnerId = task.responsible_partner?.id ?? currentPartnerId;
        const extracted =
          relationship && responsiblePartnerId
            ? extractRelationshipVariables(relationship, responsiblePartnerId)
            : undefined;
        const taskVariables = buildTaskVariables(extracted, t);
        const status = TASK_STATE_TO_STATUS[task.state];
        const ownership = getOwnership(task, currentPartnerId);
        const actionRequired = stepStatus === 'current' && ownership === 'you' && ['TODO', 'REJECTED'].includes(status);

        return {
          id: task.id,
          title: interpolateString(task.title, taskVariables),
          status,
          ownership,
          actionRequired,
          path:
            (ownership === 'you' || ownership === 'procuros') && relationshipId
              ? routeTo('taskDetails', { partnerId, relationshipId, taskId: task.id })
              : undefined,
        };
      }),
    [tasks, currentPartnerId, stepStatus, routeTo, partnerId, relationshipId, relationship, t],
  );

  return { rows };
};
